(ns manage.content-configuration-page.predicate.db
  (:require ["moment/min/moment-with-locales$default" :as moment]
            [common.spec :refer [eid?]]
            [datascript.core :as d]
            [manage.content-configuration-page.context-source.schema :as s]
            [manage.content-configuration-page.context-source.shared :as cs]
            [re-frame.core :as rf]))

(def operators
  [{:key :=
    :label "is"
    :supported-types #{:string :integer :enum :option :bool}}
   {:key :<>
    :label "is not"
    :supported-types #{:string :integer :enum :option :bool}}
   {:key :<
    :label "less than"
    :supported-types #{:integer}}
   {:key :>
    :label "greater than"
    :supported-types #{:integer}}
   {:key :in
    :label "any of"
    :supported-types #{:enum :option :string}
    :value-type-modifier :list}
   {:key :not-in
    :label "not any of"
    :supported-types #{:enum :option}
    :value-type-modifier :list}
   {:key :in-range
    :label "is between"
    :supported-types #{:integer :timestamp :time}
    :value-type-modifier :range}
   {:key :contains-none
    :label "contains none of"
    :supported-types #{:set}}
   {:key :contains-some
    :label "contains some of"
    :supported-types #{:set}}
   {:key :contains-all
    :label "contains all of"
    :supported-types #{:set}}
   {:key :available
    :label "is available"
    :supported-types #{:string :integer :enum
                       :option :bool :timestamp :time}
    :value-type-modifier :nil}])

(defn value-type-modifier [operator]
  (get-in (group-by :key operators) [operator 0 :value-type-modifier]))

(defn context-source->schema
  "reimplementation of the :resource.schema/output sub, but suitable for use in an
  event handler"
  [db {:context-source/keys [schema] :as context-source}]
  (let [param (cs/parameter context-source)]
    (merge-with merge
                (get-in db [:resource.schema/output-structure schema param])
                (get-in db [:resource.schema/output-data schema param]))))

(defn macro-type
  [db {context-source :context-source/_macro :macro/keys [selector]}]
  (let [schema (context-source->schema db context-source)
        selector-schema (s/selector->schema selector schema)
        macro-type (s/get-type selector-schema)]
    (if (vector? macro-type)
      (mapv keyword macro-type)
      (keyword macro-type))))

(defn operator-options
  [db macro]
  (when-let [macro-type (macro-type db macro)]
    (let [macro-type (cond-> macro-type (coll? macro-type) first)]
      (keep (fn [{:keys [key label supported-types]}]
              (when (contains? supported-types macro-type)
                {:value key :label label}))
            operators))))

(rf/reg-sub :predicate/operator-options
  (fn [db [_ macro]]
    (operator-options db macro)))

(defmulti initial-value
  "Construct the initial value for a value type"
  #(if (coll? %)
     (first %)
     %))

(defmethod initial-value :nil [_]
  nil)

(defmethod initial-value :enum [_]
  nil)

(defmethod initial-value :integer [_]
  0)

(defmethod initial-value :option [_]
  nil)

(defmethod initial-value :bool [_]
  true)

(defmethod initial-value :timestamp [_]
  {:timestamp (-> (moment)
                  (.startOf "day")
                  (.toISOString))})

(defmethod initial-value :time [_]
  (let [now (moment)]
    {:hour (.hour now) :minute (.minute now)}))

(defmethod initial-value :list [_]
  [])

(defmethod initial-value :set [_]
  [])

(defmethod initial-value :range [type]
  {:start (initial-value (rest type))
   :end (initial-value (rest type))})

(defmethod initial-value :default [_]
  "")

(defn value-options
  [db {context-source :context-source/_macro :macro/keys [selector]}]
  (s/find-values selector {:data {:out (context-source->schema db context-source)}}))

(rf/reg-sub :predicate/value-options
  (fn [db [_ macro]]
    (value-options db macro)))

(defn macro+operator->initial-value [db macro operator]
  (let [macro-type (macro-type db macro)
        selectable-value? (or (= macro-type :enum) (= macro-type :option))]
    (cond
      (and selectable-value? (contains? #{:in :not-in} operator))
      []

      (and selectable-value? (not= operator :available))
      (:value (first (value-options db macro)))

      :else
      (initial-value (if-let [modifier (value-type-modifier operator)]
                       [modifier macro-type]
                       macro-type)))))

(rf/reg-event-fx :predicate/edit-operator
  (fn [{:keys [db]} [_ predicate operator]]
    (let [initial-value (macro+operator->initial-value db predicate operator)]
      {:fx [[:transact [(assoc predicate :predicate/operator operator)
                        (if (nil? initial-value)
                          [:db.fn/retractAttribute (:db/id predicate) :predicate/value]
                          [:db/add (:db/id predicate) :predicate/value initial-value])]]]})))

(defn predicate [r macro operator value]
  (cond-> (merge macro {:rule/_condition r :predicate/operator operator})
    (some? value) (assoc :predicate/value value)))

(defn create-many [db ds rules macros]
  (for [macro macros
        :let [cs (:context-source/_macro macro)
              macro (cond-> macro
                      (eid? cs)
                      (assoc :context-source/_macro (d/pull ds cs/pull-pattern-context-source cs)))
              operator (-> (operator-options db macro) first :value)
              initial-value (macro+operator->initial-value db macro operator)]
        r rules]
    (predicate r macro operator initial-value)))

(rf/reg-event-fx :predicate/create
  [(rf/inject-cofx :ds)]
  (fn [{:keys [db ds]} [_ rule-or-rules macro-or-macros]]
    {:fx [[:transact (create-many
                      db ds
                      (cond-> rule-or-rules (eid? rule-or-rules) vector)
                      (cond-> macro-or-macros (map? macro-or-macros) vector))]]}))

